import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import { Sparkles, ArrowRight, Calendar as CalendarIcon, FileText, Film, Video, Smartphone, Mail, ListChecks, CheckCircle2 } from "lucide-react";
import { useAuth } from "../lib/auth";

const Workspace = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/command/workspace/me").then((r) => setData(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  if (loading) return <Layout title="Mi Workspace"><div className="flex justify-center py-20"><span className="spinner" /></div></Layout>;
  if (!data) return <Layout title="Mi Workspace"><p>Sin datos</p></Layout>;

  const s = data.stats;

  return (
    <Layout title={`Hola ${data.user?.name?.split(" ")[0] || ""}`} subtitle={`Tu workspace personalizado para ${data.role.replace(/_/g, " ")}`}>
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <StatCard icon={ListChecks} label="Tareas" value={s.tasks} url="/tareas" highlight />
        <StatCard icon={FileText} label="Briefs" value={s.briefs} url="/briefs" />
        <StatCard icon={Film} label="Guiones" value={s.scripts} url="/guiones" />
        <StatCard icon={Video} label="Videos" value={s.videos} url="/video-lab" />
        <StatCard icon={CalendarIcon} label="Publicaciones próx." value={s.calendar_pending} url="/calendario" />
        <StatCard icon={Smartphone} label="Push pendientes" value={s.pushes_pending} url="/push-marketing" />
        <StatCard icon={Mail} label="Emails pend." value={s.emails_pending} url="/email-marketing" />
        <StatCard icon={CheckCircle2} label="Total pendiente" value={s.total_pending} url="#" />
      </div>

      {/* Role tools */}
      <div className="aura-card-flat p-5 mb-5">
        <div className="label-overline mb-3">🛠 Herramientas para tu rol</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {(data.tools || []).map((t) => (
            t.coming_soon ? (
              <div key={t.key} className="aura-card-flat p-3 opacity-60 cursor-not-allowed border border-dashed border-[#D99D26]/30">
                <div className="text-sm font-medium">{t.label}</div>
                <div className="text-[10px] text-[#666] mt-1">Próximamente</div>
              </div>
            ) : (
              <Link key={t.key} to={t.url} className="aura-card-flat p-3 hover:border-[#D99D26] flex justify-between items-center" data-testid={`tool-${t.key}`}>
                <span className="text-sm font-medium">{t.label}</span>
                <ArrowRight size={14} className="text-[#8a5e10]" />
              </Link>
            )
          ))}
        </div>
      </div>

      {/* Pending items lists */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <PendingList title="📋 Mis briefs" items={data.items?.briefs} link="/briefs" testid="my-briefs" />
        <PendingList title="🎬 Mis guiones" items={data.items?.scripts} link="/guiones" testid="my-scripts" />
        <PendingList title="🎥 Mis videos" items={data.items?.videos} link="/video-lab" testid="my-videos" />
        <PendingList title="✅ Mis tareas" items={data.items?.tasks} link="/tareas" testid="my-tasks" />
        {data.items?.pushes?.length > 0 && <PendingList title="📲 Mis push" items={data.items.pushes} link="/push-marketing" testid="my-pushes" subtitleKey="target_date" />}
        {data.items?.emails?.length > 0 && <PendingList title="✉️ Mis emails" items={data.items.emails} link="/email-marketing" testid="my-emails" subtitleKey="target_date" titleKey="subject" />}
      </div>
    </Layout>
  );
};

const StatCard = ({ icon: Icon, label, value, url, highlight }) => (
  <Link to={url} className={`aura-card-flat p-3 ${highlight ? "bg-gradient-to-br from-[#FFF9E6] to-white" : ""}`}>
    <div className="flex justify-between items-center mb-1">
      <Icon size={12} className="text-[#8a5e10]" />
      <span className="text-[10px] text-[#666] uppercase tracking-wider">{label}</span>
    </div>
    <div className="font-display text-2xl font-medium">{value}</div>
  </Link>
);

const PendingList = ({ title, items, link, testid, titleKey = "title", subtitleKey = "due_date" }) => {
  if (!items || items.length === 0) return null;
  return (
    <div className="aura-card-flat p-5" data-testid={testid}>
      <div className="flex justify-between items-center mb-3">
        <div className="font-display text-sm font-medium">{title} <span className="text-[#666]">({items.length})</span></div>
        <Link to={link} className="text-xs text-[#8a5e10] font-medium">Ver todos →</Link>
      </div>
      <div className="space-y-2">
        {items.slice(0, 5).map((it) => (
          <div key={it.id} className="text-sm py-2 border-b border-[#F0F0F0] last:border-0">
            <div className="font-medium truncate">{it[titleKey] || "(sin título)"}</div>
            <div className="text-[10px] text-[#666] mt-0.5">
              {it[subtitleKey] && `${subtitleKey === 'due_date' ? '⏱' : '📅'} ${it[subtitleKey]}`}
              {it.status && <span className="ml-2 pill pill-gray text-[9px]">{it.status.replace(/_/g, " ")}</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Workspace;
